import { z } from 'zod';
import {
  ArtefactKindSchema,
  type CanonicalObjectType,
  ComponentTypeSchema,
  ControlKindSchema,
  DataClassificationSchema,
  DataSubjectSchema,
  ExtractionUnknownKindSchema,
  PrioritySchema,
  TrustLevelSchema,
} from './enums';
import {
  ActorIdSchema,
  ArtefactIdSchema,
  AssessmentIdSchema,
  AssetIdSchema,
  AssumptionIdSchema,
  ComponentIdSchema,
  DataFlowIdSchema,
  ExistingControlIdSchema,
  idKindOf,
  TenantIdSchema,
  TrustBoundaryIdSchema,
  UnknownIdSchema,
  UserIdSchema,
} from './ids';
import { ArtefactCoverageSchema } from './coverage';
import {
  ClientQuestionSchema,
  NonEmptyStringSchema,
  Sha256Schema,
  TimestampSchema,
  TristateSchema,
} from './primitives';
import {
  attributeProvenanceSchema,
  attributesMissingProvenance,
  emptyCollectionsClaimingProvenance,
  establishedAttributes,
  contradictionIsWitnessed,
  factIsCited,
  type Provenance,
  type Provenanced,
  ProvenanceSchema,
} from './provenance';

/** An uploaded input (DESIGN_SPEC §7.4). */
export const SourceArtefactSchema = z.object({
  artefact_id: ArtefactIdSchema,
  tenant_id: TenantIdSchema,
  assessment_id: AssessmentIdSchema,
  kind: ArtefactKindSchema,
  filename: z.string(),
  media_type: z.string(),
  storage_ref: NonEmptyStringSchema,
  sha256: Sha256Schema,
  size_bytes: z.int().nonnegative(),
  uploaded_by: UserIdSchema,
  uploaded_at: TimestampSchema,
  /**
   * The artefact this one replaces, when a client says so (docs/DECISIONS.md
   * ZD-069). `null` on every ordinary upload — nothing infers it: two
   * documents with different bytes are two documents unless a client asserts
   * otherwise, and guessing would be the manufactured assertion ZD-021 names.
   *
   * A forward-only pointer, set once at upload and never rewritten — the
   * identity-ledger convention (ZD-049 §3), not a second `superseded_by`
   * column on the old row. Whether an artefact is *current* is a derived view
   * over the whole set (`currentArtefacts`, below):
   * an artefact nothing points at. The superseded row, its bytes and its
   * document index are never deleted — an issued report may still cite them.
   */
  supersedes: ArtefactIdSchema.nullable(),
});
export type SourceArtefact = z.infer<typeof SourceArtefactSchema>;

/**
 * The current artefacts of an assessment: every one not named by another's
 * `supersedes` (docs/DECISIONS.md ZD-069).
 *
 * A derived view, not a stored fact — the identity-ledger convention (ZD-049
 * §3) applied to this table: currency is computed from the forward pointers
 * that exist, rather than kept as a second, independently-writable fact that
 * could disagree with them. A superseded row is never removed from the input;
 * it simply is not in the output, and its bytes and document index are
 * untouched wherever they already are (readable by artefact id, same as
 * before).
 *
 * Beside the schema rather than in `@zevik/shared`'s store since `sds-12l3`,
 * because the console lists an assessment's documents and has to say which
 * are current by the rule ingestion reads by — one derivation, two consumers
 * (ZD-124 §3). The store re-exports it.
 */
export function currentArtefacts(artefacts: readonly SourceArtefact[]): SourceArtefact[] {
  const superseded = new Set(
    artefacts.flatMap((artefact) => (artefact.supersedes ? [artefact.supersedes] : [])),
  );
  return artefacts.filter((artefact) => !superseded.has(artefact.artefact_id));
}

/**
 * Ingestion output — pre-canonical (DESIGN_SPEC §7.4, §10.1).
 *
 * Looser than the canonical model in exactly one respect: **cross-references
 * are not required to resolve.** Vision will not extract everything, and a flow
 * whose far end was never identified is a real and useful extraction result —
 * it becomes an unknown, not a validation failure. Normalisation is where
 * referential integrity bites.
 *
 * It is *not* looser about provenance. Every extracted object carries the same
 * `Provenance` the canonical model uses (docs/DECISIONS.md ZD-014), so
 * normalisation lifts provenance rather than re-deriving it, and
 * `objectConfidence()` reads an extracted object exactly as it reads a
 * canonical one. There is deliberately no `confidence` field and no
 * `confidence_summary`: confidence is computed, never reported (CLAUDE.md
 * principle 5).
 *
 * The ingestion worker is forbidden from emitting threats or recommendations
 * (§6.3); this schema is the enforcement point.
 */

/**
 * Attribute vocabularies for the extracted objects (docs/DECISIONS.md ZD-020).
 *
 * Deliberately *not* the canonical lists. Extraction holds fewer attributes,
 * and a shared list would let an extractor key provenance on a field it has no
 * value for — an entry describing how something was established when nothing
 * was. `extraction.test.ts` asserts each list against its schema's own fields.
 */
export const EXTRACTED_SYSTEM_ATTRIBUTES = [
  'name',
  'description',
  'internet_facing',
  'multi_tenant',
] as const;

export const EXTRACTED_COMPONENT_ATTRIBUTES = [
  'name',
  'type',
  'description',
  'internet_facing',
  'technologies',
] as const;

export const EXTRACTED_ACTOR_ATTRIBUTES = ['name', 'trust'] as const;

export const EXTRACTED_DATA_FLOW_ATTRIBUTES = [
  'from',
  'to',
  'protocol',
  'data',
  'asset_ids',
  'encrypted',
  'authenticated',
] as const;

export const EXTRACTED_TRUST_BOUNDARY_ATTRIBUTES = ['name', 'separates'] as const;

export const EXTRACTED_ASSET_ATTRIBUTES = [
  'name',
  'data_classification',
  'data_subject',
  'resides_in',
] as const;

export const EXTRACTED_EXISTING_CONTROL_ATTRIBUTES = ['name', 'kinds', 'applies_to'] as const;

/**
 * Which canonical attributes a **document** can establish, by object type
 * (docs/DECISIONS.md ZD-044 §4).
 *
 * The lists above, indexed, so a reader asking *"could any document have
 * answered this?"* reads the same vocabulary extraction is validated against
 * rather than a second copy of it. `Record<CanonicalObjectType, …>` is the
 * exhaustiveness check: a canonical type added without an entry does not
 * compile, so the map cannot go quietly stale the way a hand-listed scope does
 * (ZD-036 §7).
 *
 * **Three types are empty, and empty here is a claim rather than an omission.**
 * `control_flow`, `environment` and `safety_function` have no `Extracted*`
 * schema at all — a control flow is derived by normalisation, and the other two
 * are ZD-010's reserved vocabulary with no extractor yet. So *no* attribute of
 * theirs is document-established, which is exactly what an empty list says here
 * and is why the map is keyed on the canonical type rather than only on the
 * types extraction happens to cover.
 *
 * **What is absent from a type's list is the interesting half.**
 * `data_flow.crosses_boundary_ids` is not here because ZD-027 rule 3 derives it
 * from `TrustBoundary.separates`; no document states it, so a clarification
 * asking a client for it asks for something they cannot give. It is the only
 * such attribute any corpus clarification names, and finding the next one is
 * this map's job.
 */
export const EXTRACTED_ATTRIBUTES: Record<CanonicalObjectType, readonly string[]> = {
  component: EXTRACTED_COMPONENT_ATTRIBUTES,
  actor: EXTRACTED_ACTOR_ATTRIBUTES,
  asset: EXTRACTED_ASSET_ATTRIBUTES,
  data_flow: EXTRACTED_DATA_FLOW_ATTRIBUTES,
  control_flow: [],
  trust_boundary: EXTRACTED_TRUST_BOUNDARY_ATTRIBUTES,
  environment: [],
  existing_control: EXTRACTED_EXISTING_CONTROL_ATTRIBUTES,
  safety_function: [],
};

/**
 * The system the artefacts describe (docs/DECISIONS.md ZD-021).
 *
 * Extraction had no field for this at all, while `CanonicalModel.system` is
 * required and its `name` is a `NonEmptyString` — so normalisation would have
 * had to invent a name for every assessment. `vetchford/system.json` states the
 * name, the description and the multi-tenancy, and every one of them was
 * discarded.
 *
 * The whole object is nullable rather than its `name` being nullable: a diagram
 * with no title identifies no system, and a system object with no name is not a
 * weaker claim, it is not a claim. Where extraction finds none, the assessment's
 * own title is the honest source — a human typed it — and pulling it across is
 * normalisation's job, not something to fake here.
 */
export const ExtractedSystemSchema = z.object({
  name: NonEmptyStringSchema,
  description: z.string(),
  internet_facing: TristateSchema,
  multi_tenant: TristateSchema,
  provenance: ProvenanceSchema,
  attribute_provenance: attributeProvenanceSchema(EXTRACTED_SYSTEM_ATTRIBUTES),
});
export type ExtractedSystem = z.infer<typeof ExtractedSystemSchema>;

export const ExtractedComponentSchema = z.object({
  component_id: ComponentIdSchema,
  name: NonEmptyStringSchema,
  type: ComponentTypeSchema,
  /**
   * `description`, `internet_facing` and `technologies` are here because the
   * input formats state them and this schema had nowhere to put them
   * (docs/DECISIONS.md ZD-021). `internet_facing` was the expensive one: the
   * structured extractor read it *only* to decide whether to raise an unknown
   * and then dropped the value, so a component the document explicitly marks
   * internet-facing reached the canonical model as `unknown` — emptying the
   * predicate the IT pack leans on hardest.
   */
  description: z.string(),
  internet_facing: TristateSchema,
  technologies: z.array(z.string()),
  provenance: ProvenanceSchema,
  attribute_provenance: attributeProvenanceSchema(EXTRACTED_COMPONENT_ATTRIBUTES),
});
export type ExtractedComponent = z.infer<typeof ExtractedComponentSchema>;

export const ExtractedActorSchema = z.object({
  actor_id: ActorIdSchema,
  name: NonEmptyStringSchema,
  trust: TrustLevelSchema,
  provenance: ProvenanceSchema,
  attribute_provenance: attributeProvenanceSchema(EXTRACTED_ACTOR_ATTRIBUTES),
});
export type ExtractedActor = z.infer<typeof ExtractedActorSchema>;

export const ExtractedDataFlowSchema = z.object({
  flow_id: DataFlowIdSchema,
  /** May name an object this extraction never identified — see the note above. */
  from: z.string(),
  to: z.string(),
  protocol: z.string().nullable(),
  data: z.string(),
  /**
   * The assets this flow carries (docs/DECISIONS.md ZD-036).
   *
   * **Labels, not ids, and the field name is canonical's** — the same shape
   * `from` and `to` already take: extraction names things the way the document
   * does and normalisation resolves them through the ZD-026 identity map, so a
   * flow whose cargo was never declared as an asset degrades to a dropped
   * member and a question (ZD-027 rule 1) rather than a validation failure. A
   * separate extraction-side name was considered and rejected: a second
   * vocabulary is a second copy nothing can check against the first.
   *
   * `data` is **not** made redundant by this. ZD-026 §5 accepted losing the
   * more specific of two prose wordings on the ground that *"the privacy pack
   * grounds on `Asset.data_classification`, not on a flow's prose"* — an
   * argument that presupposed this field and was a promise until it shipped.
   *
   * An empty array means nobody said, and may not carry provenance of its own
   * (`emptyCollectionsClaimingProvenance`): a flow established as carrying
   * *nothing* is a denial, and the merge would discard it in any case.
   */
  asset_ids: z.array(z.string()),
  encrypted: TristateSchema,
  /**
   * Whether the calling party is authenticated to the receiving side
   * (docs/DECISIONS.md ZD-040).
   *
   * **A `Tristate`, and the shape was the decision.** A collection was the
   * obvious candidate after ZD-039 — authentication is genuinely several things
   * at once (mutual or one-way, transport or application, as which identity) —
   * and it fails on ZD-021's mirror question in the one direction that matters
   * here: ZD-036 §2 forbids extraction from establishing an **empty**
   * collection, so a collection could never carry the Tarnmouth LLD's *"requests
   * that match it are not required to present a client certificate"*. A stated
   * negative would be unrepresentable, in the field added to represent it. The
   * mechanism a collection would have held already has three homes —
   * `ExistingControl.kinds`, `protocol`'s token set (ZD-026 §4), and the
   * control's own prose — and no pattern reads mechanism.
   *
   * `false` is a claim and carries its own provenance record (ZD-020);
   * `'unknown'` is the absence-marker and inherits. That difference is the whole
   * of what `established_as` reads.
   */
  authenticated: TristateSchema,
  provenance: ProvenanceSchema,
  attribute_provenance: attributeProvenanceSchema(EXTRACTED_DATA_FLOW_ATTRIBUTES),
});
export type ExtractedDataFlow = z.infer<typeof ExtractedDataFlowSchema>;

export const ExtractedTrustBoundarySchema = z.object({
  boundary_id: TrustBoundaryIdSchema,
  /**
   * Nullable, because a boundary is routinely drawn without one
   * (docs/DECISIONS.md ZD-019).
   *
   * A dashed rectangle on a diagram with no text on it is a real boundary and
   * has no name. When this field was required, an extractor could not record
   * that — it had to invent a name to satisfy the schema, and an invented name
   * is indistinguishable downstream from one the document supplied. Requiring a
   * value the source may not contain is how a schema manufactures fabrication.
   *
   * An unnamed boundary is identified by what it `separates`. The canonical
   * model now follows suit (ZD-023), so the null is carried rather than
   * translated.
   */
  name: NonEmptyStringSchema.nullable(),
  separates: z.array(z.string()),
  provenance: ProvenanceSchema,
  attribute_provenance: attributeProvenanceSchema(EXTRACTED_TRUST_BOUNDARY_ATTRIBUTES),
});
export type ExtractedTrustBoundary = z.infer<typeof ExtractedTrustBoundarySchema>;

export const ExtractedAssetSchema = z.object({
  asset_id: AssetIdSchema,
  name: NonEmptyStringSchema,
  data_classification: DataClassificationSchema,
  /**
   * Whose data this asset concerns — the same `DataSubjectSchema` canonical
   * carries, labelled at the extraction layer rather than translated
   * (docs/DECISIONS.md ZD-076 §2). Free text, not a reference: forcing one
   * would make the extractor decide, for every document, whether "driver" and
   * an actor named "hauliers" are the same thing.
   */
  data_subject: DataSubjectSchema,
  /**
   * The components this asset rests in (docs/DECISIONS.md ZD-036). Labels, for
   * the reasons on `ExtractedDataFlow.asset_ids`.
   *
   * **The link lives on the asset only, and the documents state it from the
   * other end.** *"Transcript Store — Managed database holding support ticket
   * transcripts"* is a sentence about the component; a `Component.holds` to
   * catch it directly was considered and refused, because one fact in two
   * places is the third-fact-that-can-disagree shape ZD-017 and ZD-027 rule 3
   * both rejected. The cost is stated rather than hidden: a document naming a
   * holding component for something extraction never identified as an asset
   * records nothing.
   */
  resides_in: z.array(z.string()),
  provenance: ProvenanceSchema,
  attribute_provenance: attributeProvenanceSchema(EXTRACTED_ASSET_ATTRIBUTES),
});
export type ExtractedAsset = z.infer<typeof ExtractedAssetSchema>;

export const ExtractedExistingControlSchema = z.object({
  control_id: ExistingControlIdSchema,
  name: NonEmptyStringSchema,
  /**
   * What the control does (docs/DECISIONS.md ZD-039), under canonical's own
   * field name — the ZD-036 §1 convention.
   *
   * A collection because a control routinely does two things, and because an
   * unestablished collection is the one shape that lets *"the document names a
   * control and does not say what it does"* reach a pattern as `unknown` rather
   * than as a denial (ZD-029 §3). Extraction may not establish it empty
   * (ZD-036 §2); `checkExtractionIntegrity` refuses that with no new rule,
   * because the scope of that check is read off the schema.
   */
  kinds: z.array(ControlKindSchema),
  applies_to: z.array(z.string()),
  provenance: ProvenanceSchema,
  attribute_provenance: attributeProvenanceSchema(EXTRACTED_EXISTING_CONTROL_ATTRIBUTES),
});
export type ExtractedExistingControl = z.infer<typeof ExtractedExistingControlSchema>;

/**
 * An assumption is not a fact. Ingestion may never promote one (§6.3).
 *
 * ### This object survives the `assumed` refusal, and the distinction is the
 * whole of why it is written here rather than only in a decision entry
 *
 * ZD-105 §8 settled *"extraction emits no `assumed`"* and ZD-106 §2 implements
 * it. **That is about `ProvenanceBasis`, not about this object**, and a session
 * reading §8 literally deletes the thing the diagram path needs —
 * `structured-extractor.ts` says in its own comment that on a diagram
 * *"assumptions are how the gaps get named"*.
 *
 * A diagram extractor still declares assumptions here and marks the facts
 * resting on them `inferred`, citing the region it read and naming the
 * assumption in `derived_from_assumption_ids`; `fixtures.ts` carries exactly
 * that shape today. What the citation floor and the basis refusal say
 * **jointly** is narrower than §8's sentence, and is what §8 should have said:
 *
 * > Extraction may not assert a fact resting on nothing but an assumption.
 *
 * With nothing to cite, the answer is an `Unknown` rather than a fact — which
 * is CLAUDE.md principle 10's *"an input document that cannot answer a material
 * question is a finding, not a gap to paper over"*, arriving as a schema
 * refusal instead of as an instruction in a prompt.
 */
export const ExtractedAssumptionSchema = z.object({
  assumption_id: AssumptionIdSchema,
  statement: NonEmptyStringSchema,
  basis: NonEmptyStringSchema,
});
export type ExtractedAssumption = z.infer<typeof ExtractedAssumptionSchema>;

/** Unknowns are first-class: they drive the clarification loop (§10.2). */
export const ExtractedUnknownSchema = z.object({
  unknown_id: UnknownIdSchema,
  /**
   * The question, and it must be one (ZD-103).
   *
   * **This is the layer that matters**, because it is where a model will write
   * the field. `structuredExtractor` and `projectExtraction` both end in
   * `ExtractedArchitectureSchema.parse`, so the rule bites the corpus's own
   * fixture build at the line that wrote it — and `sds-02c` puts an LLM in the
   * same seat, where nothing else would check the sentence at all.
   */
  question_hint: ClientQuestionSchema,
  /**
   * What sort of gap this is (docs/DECISIONS.md ZD-057 §5, ZD-106 §3).
   *
   * Narrowed against canonical `Unknown`'s vocabulary: an extraction may not
   * write `sources_disagree`, because it reads one artefact's content and one
   * source cannot disagree with itself. The narrowing is a subtype, so
   * normalisation lifts this into a canonical `Unknown` with nothing to
   * convert, and `enums.ts` argues why this refusal lands on the shape while
   * its sibling `assumed` refusal lands in `checkExtractionIntegrity`.
   */
  kind: ExtractionUnknownKindSchema,
  priority: PrioritySchema,
  /**
   * The objects this question is about (docs/DECISIONS.md ZD-021).
   *
   * Canonical `Unknown` has carried this since sds-00 and the extracted form
   * did not, so normalisation had two options and both were bad: drop the link,
   * or recover it by string-parsing the unknown's own id — which is exactly the
   * id-parsing ZD-004 forbids, and which only worked at all because one
   * extractor happened to build its ids out of object ids.
   *
   * Both ZD-019 and ZD-021 are satisfiable here. When the question is about no
   * particular object — an unreadable artefact, a system-level gap — `[]` is an
   * honest answer rather than a forced one; and when it is about a component,
   * there is now somewhere to say so.
   *
   * Object ids only. An artefact is not an object: naming one here would send a
   * reader to resolve it against the object index and find nothing. An
   * artefact-scoped question names its artefact in the `question_hint` and is
   * tied to the artefact through `source_coverage`.
   */
  related_object_ids: z.array(z.string()),
  /**
   * The single extraction attribute this gap is about, or `null` where it is
   * not about exactly one (docs/DECISIONS.md ZD-107 §3).
   *
   * **The field exists before a model can raise unknowns at volume, and that
   * ordering is the whole of why it is here.** ZD-105 §2's argument, one object
   * along: once stored unknowns exist without it, *which attribute was this
   * about* is unrecoverable, because the answer was only ever in the prose of
   * `question_hint`. `sds-02c3` is the row that walks through the door.
   *
   * **What `null` promises, exactly.** Not *"this gap is about no attribute"* —
   * that would be one field doing two jobs, since a gap spanning two attributes
   * would have to take it too. It promises the weaker and decidable thing: *this
   * gap is not about exactly one named attribute.* A producer always knows,
   * because it is reporting its own reason for raising the unknown rather than
   * reading a value off a document — the same argument ZD-106 §3 makes for
   * `kind`. A consumer reading `null` learns that and nothing more, and in
   * particular may not read it as *"not an attribute question"*.
   *
   * **A scalar rather than a collection, and the mirror question decided it in
   * both directions** (ZD-040's shape, on a different object). A collection
   * could hold the two-attribute case and could not hold the negative at all:
   * `[]` meaning *not about an attribute* and `[]` meaning *nobody worked it
   * out* are ZD-036 §2's two claims, and extraction may not make the second.
   * Only a scalar can state the denial, so the two-attribute case takes `null`
   * and is under-claimed rather than misreported.
   *
   * **A free string rather than a closed vocabulary, on the mirror question
   * again.** 14 of the corpus's 58 expected unknowns are about something
   * `ExtractedArchitecture` has no field for — a retention period, a boundary
   * kind, a jurisdiction, a credential rotation — so a union over the extraction
   * vocabularies would leave a producer with a real gap to report and nothing
   * legal to write, which is ZD-019's manufactured assertion. Whether a value
   * names an attribute the schema holds is therefore measured rather than
   * refused; `unknown-questions.test.ts` is where the count lives.
   */
  about_attribute: z.string().min(1).nullable(),
});
export type ExtractedUnknown = z.infer<typeof ExtractedUnknownSchema>;

/**
 * The extracted objects carry the same ZD-020 pair, with their own exact
 * vocabularies.
 *
 * This is the assertion that keeps ZD-014's promise mechanical. Extraction and
 * the canonical model share `Provenance` so that normalisation *lifts* it
 * rather than re-deriving it, and a layer that lost `attribute_provenance` — or
 * whose vocabulary drifted from the values it actually holds — would turn that
 * lift back into a translation, which is a step where provenance can weaken.
 *
 * The vocabularies here are deliberately **not** the canonical ones: extraction
 * holds fewer attributes, and a shared list would let an extractor key
 * provenance on a field it has no value for. Key-set equality in both
 * directions is what makes that a compile error rather than a convention.
 *
 * What it still cannot see, and why that is the right split: dropping a member
 * from a vocabulary *constant* changes both sides of the comparison at once, so
 * this stays green. That drift is between a vocabulary and the schema's own
 * fields, which is a runtime question — `attribute vocabularies match their
 * schemas` in `fact-provenance.test.ts` answers it, and was confirmed to fail
 * when broken.
 */
type ExactKeys<Actual, Expected> = [Actual] extends [Expected]
  ? [Expected] extends [Actual]
    ? true
    : { vocabulary_is_missing: Exclude<Expected, Actual> }
  : { vocabulary_has_extra: Exclude<Actual, Expected> };

/**
 * `T extends Provenanced<A>` catches the field going missing; `ExactKeys`
 * catches a wrong or widened vocabulary. `AssertTrue` is what makes the second
 * half bite — an earlier version returned a sentinel *object* on failure and
 * accepted it, because an object satisfies `extends object`. It has to demand
 * `true`, and the failure branches must not be `never`, since `never` extends
 * everything and would pass silently.
 */
type AssertProvenanced<T extends Provenanced<A>, A extends string> = ExactKeys<
  keyof T['attribute_provenance'],
  A
>;

type AssertTrue<T extends true> = T;
export type AssertedProvenancedExtractedObjects = [
  AssertTrue<AssertProvenanced<ExtractedSystem, (typeof EXTRACTED_SYSTEM_ATTRIBUTES)[number]>>,
  AssertTrue<
    AssertProvenanced<ExtractedComponent, (typeof EXTRACTED_COMPONENT_ATTRIBUTES)[number]>
  >,
  AssertTrue<AssertProvenanced<ExtractedActor, (typeof EXTRACTED_ACTOR_ATTRIBUTES)[number]>>,
  AssertTrue<AssertProvenanced<ExtractedAsset, (typeof EXTRACTED_ASSET_ATTRIBUTES)[number]>>,
  AssertTrue<AssertProvenanced<ExtractedDataFlow, (typeof EXTRACTED_DATA_FLOW_ATTRIBUTES)[number]>>,
  AssertTrue<
    AssertProvenanced<ExtractedTrustBoundary, (typeof EXTRACTED_TRUST_BOUNDARY_ATTRIBUTES)[number]>
  >,
  AssertTrue<
    AssertProvenanced<
      ExtractedExistingControl,
      (typeof EXTRACTED_EXISTING_CONTROL_ATTRIBUTES)[number]
    >
  >,
];

const ExtractedArchitectureShape = z.object({
  tenant_id: TenantIdSchema,
  assessment_id: AssessmentIdSchema,
  artefact_ids: z.array(ArtefactIdSchema),
  /**
   * Null when nothing read identified a system (docs/DECISIONS.md ZD-021) — an
   * untitled diagram is the ordinary case. Normalisation fills it from the
   * assessment's own title rather than inventing one.
   */
  system: ExtractedSystemSchema.nullable(),
  components: z.array(ExtractedComponentSchema),
  actors: z.array(ExtractedActorSchema),
  data_flows: z.array(ExtractedDataFlowSchema),
  trust_boundaries: z.array(ExtractedTrustBoundarySchema),
  assets: z.array(ExtractedAssetSchema),
  existing_controls: z.array(ExtractedExistingControlSchema),
  assumptions: z.array(ExtractedAssumptionSchema),
  unknowns: z.array(ExtractedUnknownSchema),
  /**
   * How much of each artefact was actually readable (docs/DECISIONS.md ZD-025).
   * Recorded here rather than left to be re-derived from the document indexes,
   * so the extraction says plainly what it was able to see — an extraction that
   * read three of nine pages must not look identical to one that read all nine.
   */
  source_coverage: z.array(ArtefactCoverageSchema),
  extracted_at: TimestampSchema,
});

export type ExtractedArchitecture = z.infer<typeof ExtractedArchitectureShape>;

export interface ExtractionIntegrityIssue {
  code:
    | 'duplicate_id'
    | 'uncited_claim'
    | 'assumed_basis_in_extraction'
    | 'citation_outside_extraction'
    | 'unknown_assumption'
    | 'unwitnessed_contradiction'
    | 'attribute_without_provenance'
    | 'established_empty_collection'
    | 'coverage_for_unread_artefact'
    | 'duplicate_coverage';
  message: string;
  path: (string | number)[];
  id: string;
}

/**
 * Integrity for an extraction, mirroring `checkModelIntegrity` one layer up.
 *
 * Five rules. It was three, and rules 4 and 5 close a door only a model can
 * walk through — added before one could, because a schema that admits an
 * uncited claim cannot be tightened after a model has written stored objects
 * under it: a later floor refuses the false ones and the true ones alike, and
 * the missing citation is exactly the evidence that would have told them
 * apart (ZD-105 §2, ZD-106).
 *
 *  1. No duplicate ids.
 *  2. Every citation names an artefact this extraction actually read. A worker
 *     citing an artefact outside its own input is citing something it never
 *     saw. (Answers cannot appear here — extraction runs before clarification,
 *     so an `answer_*` ref at this layer is equally a fabrication.)
 *  3. A `contradicted` basis needs at least two citations, because otherwise
 *     nothing disagrees with anything (`contradictionIsWitnessed`).
 *  4. **Every fact cites something** (`factIsCited`, ZD-015, ZD-106 §1). Every
 *     extraction fact is a claim about what one document says, so there is no
 *     exemption here — unlike the canonical layer, where a fact a person
 *     asserted and a fact derived over the model are legitimately uncited and
 *     are outside the rule rather than exceptions to it.
 *  5. **No `assumed` basis** (ZD-106 §2). Reading a document does not produce
 *     assumptions; normalisation and clarification are where they legitimately
 *     arise. Rules 4 and 5 together say the narrow thing ZD-105 §8's sentence
 *     was reaching for: *extraction may not assert a fact resting on nothing
 *     but an assumption*, and with nothing to cite the answer is an
 *     `Unknown` — see `ExtractedAssumptionSchema`, which survives both.
 *
 * **Not** checked: that `from`/`to`/`separates`/`applies_to` resolve. That
 * looseness is the point of the extraction layer — see the note on the schema.
 */
export function checkExtractionIntegrity(
  extraction: ExtractedArchitecture,
): ExtractionIntegrityIssue[] {
  const issues: ExtractionIntegrityIssue[] = [];
  const seen = new Set<string>();

  const declare = (id: string, path: (string | number)[]): void => {
    if (seen.has(id)) {
      issues.push({ code: 'duplicate_id', message: `duplicate object id ${id}`, path, id });
    }
    seen.add(id);
  };

  extraction.components.forEach((o, i) => declare(o.component_id, ['components', i]));
  extraction.actors.forEach((o, i) => declare(o.actor_id, ['actors', i]));
  extraction.assets.forEach((o, i) => declare(o.asset_id, ['assets', i]));
  extraction.data_flows.forEach((o, i) => declare(o.flow_id, ['data_flows', i]));
  extraction.trust_boundaries.forEach((o, i) => declare(o.boundary_id, ['trust_boundaries', i]));
  extraction.existing_controls.forEach((o, i) => declare(o.control_id, ['existing_controls', i]));
  extraction.assumptions.forEach((o, i) => declare(o.assumption_id, ['assumptions', i]));
  extraction.unknowns.forEach((o, i) => declare(o.unknown_id, ['unknowns', i]));

  const artefactIds = new Set(extraction.artefact_ids);
  const assumptionIds = new Set(extraction.assumptions.map((a) => a.assumption_id));

  /** One provenance record, wherever it hangs. `path` already names it. */
  const checkOneProvenance = (
    provenance: Provenance,
    id: string,
    label: string,
    path: (string | number)[],
  ): void => {
    provenance.evidence_citations.forEach((citation, i) => {
      if (idKindOf(citation.ref) !== 'artefact' || !artefactIds.has(citation.ref)) {
        issues.push({
          code: 'citation_outside_extraction',
          message: `provenance cites ${citation.ref}, which is not one of the artefacts this extraction read`,
          path: [...path, 'evidence_citations', i, 'ref'],
          id: citation.ref,
        });
      }
    });
    provenance.derived_from_assumption_ids.forEach((assumptionId, i) => {
      if (!assumptionIds.has(assumptionId)) {
        issues.push({
          code: 'unknown_assumption',
          message: `provenance cites unknown assumption ${assumptionId}`,
          path: [...path, 'derived_from_assumption_ids', i],
          id: assumptionId,
        });
      }
    });
    if (!factIsCited(provenance)) {
      issues.push({
        code: 'uncited_claim',
        message: `${label} asserts a ${provenance.basis} fact and cites nothing; a fact read from a document has to be able to say where`,
        path: [...path, 'evidence_citations'],
        id,
      });
    }
    if (provenance.basis === 'assumed') {
      issues.push({
        code: 'assumed_basis_in_extraction',
        message: `${label} is marked assumed; extraction reads documents and may not assume, so a fact with nothing behind it is an unknown rather than a fact`,
        path: [...path, 'basis'],
        id,
      });
    }
    if (!contradictionIsWitnessed(provenance)) {
      issues.push({
        code: 'unwitnessed_contradiction',
        message: `${label} is marked contradicted but cites fewer than two sources; nothing disagrees with anything`,
        path: [...path, 'basis'],
        id,
      });
    }
  };

  /**
   * An object's own provenance and one record per separately established
   * attribute (docs/DECISIONS.md ZD-020).
   *
   * The attribute records get the *same* checks, which matters most for
   * `contradictionIsWitnessed`: per-attribute provenance is exactly where a
   * `contradicted` basis will now be written, so leaving those unchecked would
   * move the single-source contradiction straight back into the blind spot the
   * rule was written to cover.
   */
  const checkProvenance = <A extends string>(
    object: Provenanced<A>,
    attributes: readonly A[],
    id: string,
    path: (string | number)[],
  ): void => {
    checkOneProvenance(object.provenance, id, id, [...path, 'provenance']);
    for (const attribute of establishedAttributes(object)) {
      const provenance = object.attribute_provenance[attribute];
      if (provenance === undefined) continue;
      checkOneProvenance(provenance, id, `${id}.${attribute}`, [
        ...path,
        'attribute_provenance',
        attribute,
      ]);
    }

    // Inheritance may only weaken (docs/DECISIONS.md ZD-020) — and this layer
    // is where it matters most, because extraction is where a model is asked to
    // read a document. An LLM extractor that could inherit the object's
    // citation for an attribute the document never mentions would be able to
    // assert a fact and have the schema credit it to a real page.
    for (const attribute of attributesMissingProvenance(object, attributes)) {
      issues.push({
        code: 'attribute_without_provenance',
        message: `${id}.${String(attribute)} asserts a value but carries no provenance of its own; it would inherit the object's, which records only how the object's existence was established`,
        path: [...path, 'attribute_provenance', String(attribute)],
        id,
      });
    }

    // And the mirror prohibition (docs/DECISIONS.md ZD-036). An empty
    // collection carrying its own record is an extractor claiming the
    // collection was *established as empty* — a denial, which ZD-029 §3 then
    // reads as `false` rather than `unknown` wherever a pattern navigates it.
    // Extraction may not make that claim: normalisation's merge discards it
    // (`isStated([])` is false), so it would read as recorded and behave as
    // absent.
    for (const attribute of emptyCollectionsClaimingProvenance(object, attributes)) {
      issues.push({
        code: 'established_empty_collection',
        message: `${id}.${String(attribute)} is empty but carries provenance of its own, which claims it was established as empty; extraction may not assert an absence, and the merge would discard the record in any case`,
        path: [...path, 'attribute_provenance', String(attribute)],
        id,
      });
    }
  };

  if (extraction.system !== null) {
    checkProvenance(extraction.system, EXTRACTED_SYSTEM_ATTRIBUTES, 'system', ['system']);
  }

  extraction.components.forEach((o, i) =>
    checkProvenance(o, EXTRACTED_COMPONENT_ATTRIBUTES, o.component_id, ['components', i]),
  );
  extraction.actors.forEach((o, i) =>
    checkProvenance(o, EXTRACTED_ACTOR_ATTRIBUTES, o.actor_id, ['actors', i]),
  );
  extraction.assets.forEach((o, i) =>
    checkProvenance(o, EXTRACTED_ASSET_ATTRIBUTES, o.asset_id, ['assets', i]),
  );
  extraction.data_flows.forEach((o, i) =>
    checkProvenance(o, EXTRACTED_DATA_FLOW_ATTRIBUTES, o.flow_id, ['data_flows', i]),
  );
  extraction.trust_boundaries.forEach((o, i) =>
    checkProvenance(o, EXTRACTED_TRUST_BOUNDARY_ATTRIBUTES, o.boundary_id, ['trust_boundaries', i]),
  );
  extraction.existing_controls.forEach((o, i) =>
    checkProvenance(o, EXTRACTED_EXISTING_CONTROL_ATTRIBUTES, o.control_id, [
      'existing_controls',
      i,
    ]),
  );

  // Coverage has to be about an artefact this extraction admits to having
  // looked at (docs/DECISIONS.md ZD-025). Coverage for an unlisted artefact
  // would put a denominator in a report for a document that was not part of
  // this run.
  const covered = new Set<string>();
  extraction.source_coverage.forEach((coverage, i) => {
    if (covered.has(coverage.artefact_id)) {
      issues.push({
        code: 'duplicate_coverage',
        message: `source_coverage names ${coverage.artefact_id} more than once`,
        path: ['source_coverage', i, 'artefact_id'],
        id: coverage.artefact_id,
      });
    }
    covered.add(coverage.artefact_id);

    if (!artefactIds.has(coverage.artefact_id)) {
      issues.push({
        code: 'coverage_for_unread_artefact',
        message: `source_coverage describes ${coverage.artefact_id}, which is not one of the artefacts this extraction read`,
        path: ['source_coverage', i, 'artefact_id'],
        id: coverage.artefact_id,
      });
    }
  });

  return issues;
}

export const ExtractedArchitectureSchema = ExtractedArchitectureShape.superRefine(
  (extraction, ctx) => {
    for (const issue of checkExtractionIntegrity(extraction)) {
      ctx.addIssue({ code: 'custom', message: issue.message, path: issue.path });
    }
  },
);

/** Clarification worker output (DESIGN_SPEC §6.4). */
export const ClarificationQuestionSchema = z.object({
  question: NonEmptyStringSchema,
  reason: NonEmptyStringSchema,
  related_object_id: z.string().nullable(),
  related_unknown_id: UnknownIdSchema.nullable(),
  priority: PrioritySchema,
});
export type ClarificationQuestion = z.infer<typeof ClarificationQuestionSchema>;

export const ClarificationSetSchema = z.object({
  tenant_id: TenantIdSchema,
  assessment_id: AssessmentIdSchema,
  round: z.int().positive(),
  questions: z.array(ClarificationQuestionSchema),
});
export type ClarificationSet = z.infer<typeof ClarificationSetSchema>;

/**
 * The clarification loop is bounded so a sparse diagram cannot trap a user in an
 * endless interview (§10.2).
 */
export const MAX_CLARIFICATION_ROUNDS = 3;
